import React from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { getPaginationRange } from 'app/KinerjaShared/utils/pagination'

export interface PaginationProps {
  /** Total number of items */
  totalItems: number
  /** Number of items per page */
  pageSize: number
  /** Current page number (1-based) */
  currentPage: number
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /** Number of sibling pages to show around current page */
  siblingCount?: number
  /** Custom label for items (e.g., "pegawai", "data") */
  itemLabel?: string
  /** Whether to show the item count summary */
  showItemCount?: boolean
  /** Custom class name for the container */
  className?: string
  /** Whether pagination is disabled */
  disabled?: boolean
}

export const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
  siblingCount = 1,
  itemLabel = 'item',
  showItemCount = true,
  className = '',
  disabled = false,
}) => {
  const { totalPages, clampedPage, startIndex, endIndex } = getPaginationRange(
    totalItems,
    pageSize,
    currentPage
  )

  // Build page list with ellipsis
  const firstPage = 1
  const lastPage = totalPages
  const leftSibling = Math.max(clampedPage - siblingCount, firstPage)
  const rightSibling = Math.min(clampedPage + siblingCount, lastPage)

  const showLeftEllipsis = leftSibling > firstPage + 1
  const showRightEllipsis = rightSibling < lastPage - 1

  const middlePages = range(leftSibling, rightSibling)
  const pages: (number | 'ellipsis')[] = []

  if (totalPages > 1) {
    pages.push(firstPage)

    if (showLeftEllipsis) {
      pages.push('ellipsis')
    }

    // Add middle pages (excluding first and last if they're already included)
    middlePages.forEach((page) => {
      if (page !== firstPage && page !== lastPage) {
        pages.push(page)
      }
    })

    if (showRightEllipsis) {
      pages.push('ellipsis')
    }

    if (lastPage !== firstPage) {
      pages.push(lastPage)
    }
  }

  const handlePageChange = (page: number) => {
    if (!disabled && page >= 1 && page <= totalPages && page !== clampedPage) {
      onPageChange(page)
    }
  }

  // Don't render pagination if there's only one page and showItemCount is false
  if (totalPages <= 1 && !showItemCount) {
    return null
  }

  return (
    <div
      className={clsx(
        'flex flex-col sm:flex-row items-center justify-between gap-4',
        className
      )}
    >
      {/* Item count summary */}
      {showItemCount && (
        <Text>
          Menampilkan{' '}
          <strong>
            {startIndex}–{endIndex}
          </strong>{' '}
          dari <strong>{totalItems}</strong> {itemLabel}
        </Text>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <nav
          className="flex items-center gap-6"
          role="navigation"
          aria-label="Pagination Navigation"
        >
          {/* Previous page button */}
          <Button
            color="white"
            onClick={() => handlePageChange(clampedPage - 1)}
            disabled={disabled || clampedPage <= 1}
            aria-label="Halaman sebelumnya"
            className="!rounded-full"
            iconOnly
          >
            <Icon>chevron_left</Icon>
          </Button>

          {/* Page numbers */}
          <div className="flex items-center gap-2">
            {pages.map((page, index) => {
              if (page === 'ellipsis') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-2 text-sm text-gray-500 select-none"
                    aria-hidden="true"
                  >
                    …
                  </span>
                )
              }

              const isActive = page === clampedPage

              return (
                <Button
                  key={page}
                  color={isActive ? 'blue' : 'white'}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  disabled={disabled}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={`Halaman ${page}`}
                  className="!min-w-9 h-9"
                >
                  {page}
                </Button>
              )
            })}
          </div>

          {/* Next page button */}
          <Button
            color="white"
            disabled={disabled || clampedPage >= totalPages}
            aria-label="Halaman berikutnya"
            className="!rounded-full"
            iconOnly
            onClick={() => handlePageChange(clampedPage + 1)}
          >
            <Icon className="leading-1">chevron_right</Icon>
          </Button>
        </nav>
      )}
    </div>
  )
}

export default Pagination

/**
 * Utility function to generate array of numbers
 */
function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}
